import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { Store, createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import Modal from '../Modal/Modal';
import Btn from '../Btn/Btn';
import SpinnerBox from '../Spinner/SpinnerBox';
import { QuestionnaireResponse, QuestionnaireResponseItem, Questionnaire } from '../../types/fhir';
import { ReferoContainer } from '@helsenorge/refero/components';
import { getResources } from '../../locales/referoResources';
import rootReducer from '@helsenorge/refero/reducers';
import { getPractitionerQuestionnaireById } from '../../services/practitionerService';
import { MOCK_QUESTIONNAIRE, USE_MOCK_DATA } from '../../mocks/mockData';
import { buildQuestionnaireResponseCsv } from '../../helpers/questionnaireResponseCsv';
import { downloadBlob } from '../../helpers/downloadBlob';
import { createResponsePdf } from '../../helpers/pdf/renderPdf';
import {
    QuestionnaireKind,
    isNachsorgeKind,
    isTherapyMaterialKind,
    usesEmbeddedMaterialRenderer,
} from '../../types/QuestionnaireKind';
import TherapyMaterialRenderer from '../TherapyMaterial/TherapyMaterialRenderer';
import { TherapyMaterialAudience } from '../TherapyMaterial/therapyMaterial';
import { isExpiredUnanswered } from '../FrontPage/utils';
import './TaskResponseModal.css';

type TaskInfo = {
    id: string;
    patientName?: string;
    patientId?: string;
    questionnaireName?: string;
    questionnaireId?: string;
    status?: string;
    updatedAt?: string;
    startsOn?: string;
    endsOn?: string;
    hasResponse?: boolean;
};

type Props = {
    task: TaskInfo;
    questionnaireResponse: QuestionnaireResponse | null;
    isLoading: boolean;
    error: string | null;
    onClose: () => void;
    accessToken?: string;
    resourceKind: QuestionnaireKind;
};

const TaskResponseModal = ({
    task,
    questionnaireResponse,
    isLoading,
    error,
    onClose,
    accessToken,
    resourceKind,
}: Props): JSX.Element => {
    const { t, i18n } = useTranslation();
    const isTherapyMaterial = isTherapyMaterialKind(resourceKind);
    const isNachsorge = isNachsorgeKind(resourceKind);
    const usesEmbeddedMaterial = usesEmbeddedMaterialRenderer(resourceKind);
    const [availabilityNow, setAvailabilityNow] = useState(() => Date.now());
    const expiredUnanswered = isExpiredUnanswered(task, availabilityNow);
    const store: Store = createStore(rootReducer, applyMiddleware(thunk));
    const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
    const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false);
    const [questionnaireError, setQuestionnaireError] = useState<string | null>(null);
    const [referoKey] = useState<string>(Math.random().toString());
    const [isExportingCsv, setIsExportingCsv] = useState(false);
    const [csvExportError, setCsvExportError] = useState<string | null>(null);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [pdfExportError, setPdfExportError] = useState<string | null>(null);
    const [materialAudience, setMaterialAudience] = useState<TherapyMaterialAudience | undefined>();

    useEffect(() => {
        if (!task.endsOn || task.hasResponse) {
            return;
        }
        const deadline = new Date(task.endsOn).getTime();
        if (!Number.isFinite(deadline) || deadline <= availabilityNow) {
            return;
        }

        const timeout = window.setTimeout(
            () => setAvailabilityNow(Date.now()),
            Math.min(Math.max(deadline - availabilityNow + 10, 10), 2147483647),
        );
        return () => window.clearTimeout(timeout);
    }, [task.endsOn, task.hasResponse, availabilityNow]);

    const formatAnswer = (answer: QuestionnaireResponseItem['answer']): string => {
        if (!answer || answer.length === 0) {
            return t('No answer provided');
        }

        return answer
            .map((a) => {
                if (a.valueString) return a.valueString;
                if (a.valueInteger !== undefined) return a.valueInteger.toString();
                if (a.valueDecimal !== undefined) return a.valueDecimal.toString();
                if (a.valueBoolean !== undefined) return a.valueBoolean ? t('Yes') : t('No');
                if (a.valueDate) return a.valueDate;
                if (a.valueDateTime) return a.valueDateTime;
                if (a.valueTime) return a.valueTime;
                if (a.valueUri) return a.valueUri;
                if (a.valueCoding) {
                    return a.valueCoding.display || a.valueCoding.code || '';
                }
                if (a.valueReference) {
                    return a.valueReference.display || a.valueReference.reference || '';
                }
                if (a.valueQuantity) {
                    const qty = a.valueQuantity;
                    return `${qty.value || ''} ${qty.unit || qty.code || ''}`.trim();
                }
                if (a.valueAttachment) {
                    return a.valueAttachment.title || a.valueAttachment.url || t('Attachment');
                }
                return t('Unknown value type');
            })
            .join(', ');
    };

    const renderResponseItems = (items: QuestionnaireResponseItem[] | undefined, level = 0): JSX.Element[] => {
        if (!items || items.length === 0) {
            return [];
        }

        return items.map((item, index) => {
            const hasAnswers = item.answer && item.answer.length > 0;
            const hasNestedItems = item.item && item.item.length > 0;
            const indentClass = level > 0 ? `task-response__item--nested task-response__item--level-${level}` : '';

            return (
                <div key={`${item.linkId || index}-${level}`} className={`task-response__item ${indentClass}`}>
                    {item.linkId && (
                        <div className="task-response__question">
                            <span className="task-response__link-id">{item.linkId}</span>
                            {item.text && <span className="task-response__text">{item.text}</span>}
                        </div>
                    )}
                    {hasAnswers && (
                        <div className="task-response__answer">
                            <strong>{t('Answer')}:</strong> {formatAnswer(item.answer)}
                        </div>
                    )}
                    {hasNestedItems && (
                        <div className="task-response__nested">{renderResponseItems(item.item, level + 1)}</div>
                    )}
                    {!hasAnswers && !hasNestedItems && item.linkId && (
                        <div className="task-response__answer task-response__answer--empty">
                            {t('No answer provided')}
                        </div>
                    )}
                </div>
            );
        });
    };

    const getStatusBadgeClass = (status?: string): string => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'task-response__badge--completed';
            case 'in-progress':
                return 'task-response__badge--in-progress';
            case 'amended':
                return 'task-response__badge--amended';
            case 'entered-in-error':
                return 'task-response__badge--error';
            default:
                return 'task-response__badge--default';
        }
    };

    const handleDownloadCsv = (): void => {
        if (!questionnaireResponse) {
            return;
        }

        setCsvExportError(null);
        setIsExportingCsv(true);

        try {
            const { filename, csv } = buildQuestionnaireResponseCsv({
                task,
                questionnaireResponse,
                questionnaire: questionnaire || undefined,
            });
            downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
        } catch (err) {
            const message = err instanceof Error ? err.message : t('Unknown error');
            setCsvExportError(message);
        } finally {
            setIsExportingCsv(false);
        }
    };

    const handleDownloadPdf = async (): Promise<void> => {
        if (!questionnaireResponse) {
            return;
        }

        setPdfExportError(null);
        setIsExportingPdf(true);

        try {
            const { blob, filename } = await createResponsePdf({
                task,
                kind: resourceKind,
                questionnaire,
                questionnaireResponse,
                audience: materialAudience,
                t,
                locale: i18n.language || 'en-US',
                exportedAt: new Date(),
            });
            downloadBlob(blob, filename);
        } catch (err) {
            const message = err instanceof Error ? err.message : t('Unknown error');
            setPdfExportError(message);
        } finally {
            setIsExportingPdf(false);
        }
    };

    // Fetch Questionnaire to get name/title for display
    useEffect(() => {
        const fetchQuestionnaire = async () => {
            // Fetch questionnaire if we have questionnaireId, regardless of questionnaireResponse
            if (!task.questionnaireId) {
                return;
            }

            // Only fetch if we don't already have it
            if (questionnaire) {
                return;
            }

            setIsLoadingQuestionnaire(true);
            setQuestionnaireError(null);

            try {
                // Use mock questionnaire if mock data is enabled
                if (USE_MOCK_DATA && task.questionnaireId === '47553719-6951-4022-af2b-c40b47eb8405') {
                    console.log('[TaskResponseModal] Using mock questionnaire data');
                    setQuestionnaire(MOCK_QUESTIONNAIRE as Questionnaire);
                    setIsLoadingQuestionnaire(false);
                    return;
                }

                // Fetch from API if not using mock data
                if (!accessToken) {
                    setQuestionnaireError(
                        isNachsorge
                            ? t('You must be logged in to view aftercare material.')
                            : isTherapyMaterial
                            ? t('You must be logged in to view therapy material.')
                            : t('You must be logged in to view questionnaires.'),
                    );
                    setIsLoadingQuestionnaire(false);
                    return;
                }

                const fetchedQuestionnaire = await getPractitionerQuestionnaireById<Questionnaire>(
                    task.questionnaireId,
                    { accessToken },
                );
                setQuestionnaire(fetchedQuestionnaire);
            } catch (err) {
                const message = err instanceof Error ? err.message : t('Unknown error');
                setQuestionnaireError(message);
                console.error('[TaskResponseModal] Error loading Questionnaire:', err);
            } finally {
                setIsLoadingQuestionnaire(false);
            }
        };

        fetchQuestionnaire();
    }, [task.questionnaireId, accessToken, questionnaire, isNachsorge, isTherapyMaterial, t]);

    // Programmatically disable all form elements to ensure read-only mode
    useEffect(() => {
        if (usesEmbeddedMaterial || !questionnaire || !questionnaireResponse) {
            return;
        }

        // Function to process form elements
        const processFormElements = () => {
            const referoContainer = document.querySelector('.task-response__refero-container');
            if (!referoContainer) return;
            // Disable all input elements
            const inputs = referoContainer.querySelectorAll('input, textarea, select, button');
            inputs.forEach((element) => {
                if (
                    element instanceof HTMLInputElement ||
                    element instanceof HTMLTextAreaElement ||
                    element instanceof HTMLSelectElement ||
                    element instanceof HTMLButtonElement
                ) {
                    element.disabled = true;
                    element.setAttribute('readonly', 'readonly');
                    element.setAttribute('disabled', 'disabled');
                    // Prevent any event handlers
                    element.onclick = null;
                    element.onchange = null;
                    element.oninput = null;
                    element.onfocus = (e) => e.preventDefault();
                }
            });

            // Auto-resize textareas to fit content
            const textareas = referoContainer.querySelectorAll('textarea');
            textareas.forEach((textarea) => {
                if (textarea instanceof HTMLTextAreaElement) {
                    // Force styles to show all content
                    textarea.style.setProperty('height', 'auto', 'important');
                    textarea.style.setProperty('min-height', '100px', 'important');
                    textarea.style.setProperty('overflow', 'hidden', 'important');
                    textarea.style.setProperty('resize', 'none', 'important');

                    // Calculate and set proper height
                    const scrollHeight = textarea.scrollHeight;
                    textarea.style.setProperty('height', `${Math.max(scrollHeight + 20, 100)}px`, 'important');
                }
            });

            // Transform text inputs to show full content
            const textInputs = referoContainer.querySelectorAll(
                'input[type="text"], input[type="email"], input[type="tel"], input[type="url"], input:not([type])',
            );
            textInputs.forEach((input) => {
                if (input instanceof HTMLInputElement && input.value) {
                    const value = input.value;

                    // Check if already replaced to avoid duplicates
                    if ((input.nextSibling as HTMLElement)?.classList?.contains('read-only-input-display')) {
                        return;
                    }

                    // Create a replacement div to display the text properly
                    const displayDiv = document.createElement('div');
                    displayDiv.className = 'read-only-input-display';
                    displayDiv.textContent = value;
                    displayDiv.style.cssText = `
                        width: 100%;
                        max-width: 100%;
                        min-height: 3em;
                        padding: 0.75rem;
                        background-color: #f5f5f5;
                        border: 1px solid #d7d7d7;
                        border-radius: 4px;
                        white-space: pre-wrap;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        line-height: 1.5;
                        font-size: 1rem;
                        color: #333;
                        font-family: inherit;
                        cursor: not-allowed;
                        opacity: 0.9;
                        box-sizing: border-box;
                    `;

                    // Replace the input with the div
                    if (input.parentNode) {
                        input.style.display = 'none';
                        input.parentNode.insertBefore(displayDiv, input.nextSibling);
                    }
                }
            });

            // Add event listeners to prevent any interaction
            const preventInteraction = (e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };

            referoContainer.addEventListener('click', preventInteraction, true);
            referoContainer.addEventListener('mousedown', preventInteraction, true);
            referoContainer.addEventListener('keydown', preventInteraction, true);
            referoContainer.addEventListener('input', preventInteraction, true);
            referoContainer.addEventListener('change', preventInteraction, true);
        };

        // Initial processing with longer delay to ensure DOM is ready
        const timer = setTimeout(processFormElements, 800);

        // Also observe for any DOM changes and reprocess
        const referoContainer = document.querySelector('.task-response__refero-container');
        let observer: MutationObserver | null = null;

        if (referoContainer) {
            observer = new MutationObserver((mutations) => {
                // Check if new form elements were added
                const hasNewInputs = mutations.some((mutation) =>
                    Array.from(mutation.addedNodes).some(
                        (node) =>
                            node instanceof HTMLElement &&
                            (node.tagName === 'INPUT' ||
                                node.tagName === 'TEXTAREA' ||
                                node.querySelector('input, textarea')),
                    ),
                );

                if (hasNewInputs) {
                    setTimeout(processFormElements, 200);
                }
            });

            observer.observe(referoContainer, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            clearTimeout(timer);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [usesEmbeddedMaterial, questionnaire, questionnaireResponse, referoKey]);

    const currentLanguage = i18n.language || 'en-US';

    return (
        <Modal
            title={
                isNachsorge
                    ? t('Aftercare Response Details')
                    : isTherapyMaterial
                    ? t('Therapy Material Response Details')
                    : t('Task Response Details')
            }
            close={onClose}
            size="large"
        >
            <div className={`task-response ${questionnaire ? 'task-response--with-refero' : ''}`}>
                {/* Task Information Section */}
                <div className="task-response__section">
                    <h3 className="task-response__section-title">
                        {usesEmbeddedMaterial ? t('Assignment Information') : t('Task Information')}
                    </h3>
                    <div className="task-response__info-grid">
                        <div className="task-response__info-item">
                            <span className="task-response__info-label">{t('Patient')}:</span>
                            <span className="task-response__info-value">
                                {task.patientName || task.patientId || t('Unknown')}
                            </span>
                        </div>
                        <div className="task-response__info-item">
                            <span className="task-response__info-label">
                                {isNachsorge
                                    ? t('Aftercare material')
                                    : isTherapyMaterial
                                    ? t('Therapy Material')
                                    : t('Questionnaire')}
                                :
                            </span>
                            <span className="task-response__info-value">
                                {questionnaire?.name?.trim() ||
                                    questionnaire?.title?.trim() ||
                                    task.questionnaireName ||
                                    task.questionnaireId ||
                                    t('Unknown')}
                            </span>
                        </div>
                        <div className="task-response__info-item">
                            <span className="task-response__info-label">{t('Task Status')}:</span>
                            <span className={`task-response__badge ${getStatusBadgeClass(task.status)}`}>
                                {task.status || t('Unknown')}
                            </span>
                        </div>
                        {expiredUnanswered && (
                            <div className="task-response__info-item">
                                <span className="task-response__info-label">{t('Availability')}:</span>
                                <span className="task-response__badge task-response__badge--expired">
                                    {t('Expired – unanswered')}
                                </span>
                            </div>
                        )}
                        {task.startsOn && (
                            <div className="task-response__info-item">
                                <span className="task-response__info-label">{t('Available from')}:</span>
                                <span className="task-response__info-value">
                                    {new Date(task.startsOn).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {task.endsOn && (
                            <div className="task-response__info-item">
                                <span className="task-response__info-label">{t('Available until')}:</span>
                                <span className="task-response__info-value">
                                    {new Date(task.endsOn).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {task.updatedAt && (
                            <div className="task-response__info-item">
                                <span className="task-response__info-label">{t('Last Updated')}:</span>
                                <span className="task-response__info-value">
                                    {new Date(task.updatedAt).toLocaleString()}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <div className="task-response__loading">
                        <SpinnerBox />
                        <p>{t('Loading response...')}</p>
                    </div>
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div className="task-response__error">
                        <p>
                            {t('Failed to load response')}: {error}
                        </p>
                        {task.status === 'requested' && !usesEmbeddedMaterial && (
                            <p className="task-response__error-hint">
                                {t('This task has not been completed yet. No response is available.')}
                            </p>
                        )}
                    </div>
                )}

                {/* Response Content */}
                {questionnaireResponse && !isLoading && !error && (
                    <>
                        {/* Response Metadata */}
                        <div className="task-response__section">
                            <div className="task-response__section-title-row">
                                <h3 className="task-response__section-title">
                                    {usesEmbeddedMaterial ? t('Latest Response') : t('Response Information')}
                                </h3>
                                <div className="task-response__section-actions">
                                    <Btn
                                        title={isExportingCsv ? t('Exporting CSV...') : t('Download CSV')}
                                        variant="secondary"
                                        onClick={handleDownloadCsv}
                                        disabled={isExportingCsv || !questionnaireResponse}
                                    />
                                    <Btn
                                        title={isExportingPdf ? t('Creating PDF...') : t('Download PDF')}
                                        variant="secondary"
                                        onClick={handleDownloadPdf}
                                        disabled={isExportingPdf || isLoadingQuestionnaire || !questionnaireResponse}
                                    />
                                </div>
                            </div>
                            {csvExportError && (
                                <div className="task-response__error task-response__error--inline">
                                    <p>
                                        {t('Failed to export CSV')}: {csvExportError}
                                    </p>
                                </div>
                            )}
                            {pdfExportError && (
                                <div className="task-response__error task-response__error--inline">
                                    <p>
                                        {t('Failed to create PDF')}: {pdfExportError}
                                    </p>
                                </div>
                            )}
                            <div className="task-response__info-grid">
                                {questionnaireResponse.id && (
                                    <div className="task-response__info-item">
                                        <span className="task-response__info-label">{t('Response ID')}:</span>
                                        <span className="task-response__info-value">{questionnaireResponse.id}</span>
                                    </div>
                                )}
                                {questionnaireResponse.status && (
                                    <div className="task-response__info-item">
                                        <span className="task-response__info-label">{t('Response Status')}:</span>
                                        <span
                                            className={`task-response__badge ${getStatusBadgeClass(
                                                questionnaireResponse.status,
                                            )}`}
                                        >
                                            {questionnaireResponse.status}
                                        </span>
                                    </div>
                                )}
                                {questionnaireResponse.authored && (
                                    <div className="task-response__info-item">
                                        <span className="task-response__info-label">{t('Submitted')}:</span>
                                        <span className="task-response__info-value">
                                            {new Date(questionnaireResponse.authored).toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                {questionnaireResponse.author?.display && (
                                    <div className="task-response__info-item">
                                        <span className="task-response__info-label">{t('Author')}:</span>
                                        <span className="task-response__info-value">
                                            {questionnaireResponse.author.display}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Embedded therapy and aftercare material is rendered with response values inline. */}
                        {usesEmbeddedMaterial ? (
                            <div className="task-response__section">
                                <h3 className="task-response__section-title">
                                    {isNachsorge
                                        ? t('Aftercare Material and Answers')
                                        : t('Therapy Material and Answers')}
                                </h3>
                                {isLoadingQuestionnaire ? (
                                    <div className="task-response__loading">
                                        <SpinnerBox />
                                        <p>
                                            {isNachsorge
                                                ? t('Loading aftercare material...')
                                                : t('Loading therapy material...')}
                                        </p>
                                    </div>
                                ) : questionnaireError ? (
                                    <div className="task-response__error">
                                        <p>
                                            {isNachsorge
                                                ? t('Failed to load aftercare material')
                                                : t('Failed to load therapy material')}
                                            : {questionnaireError}
                                        </p>
                                        <p className="task-response__error-hint">
                                            {t('Displaying answers in simplified format.')}
                                        </p>
                                        <div className="task-response__answers">
                                            {renderResponseItems(questionnaireResponse.item)}
                                        </div>
                                    </div>
                                ) : questionnaire ? (
                                    <TherapyMaterialRenderer
                                        questionnaire={questionnaire}
                                        questionnaireResponse={questionnaireResponse}
                                        kind={isNachsorge ? 'nachsorge' : 'therapy-material'}
                                        onAudienceChange={setMaterialAudience}
                                    />
                                ) : (
                                    <div className="task-response__answers">
                                        {renderResponseItems(questionnaireResponse.item)}
                                    </div>
                                )}
                            </div>
                        ) : questionnaireResponse.item && questionnaireResponse.item.length > 0 ? (
                            <div className="task-response__section">
                                <h3 className="task-response__section-title">{t('Answers')}</h3>
                                {isLoadingQuestionnaire ? (
                                    <div className="task-response__loading">
                                        <SpinnerBox />
                                        <p>{t('Loading questionnaire...')}</p>
                                    </div>
                                ) : questionnaireError ? (
                                    <div className="task-response__error">
                                        <p>
                                            {t('Failed to load questionnaire')}: {questionnaireError}
                                        </p>
                                        <p className="task-response__error-hint">
                                            {t('Displaying answers in simplified format.')}
                                        </p>
                                        <div className="task-response__answers">
                                            {renderResponseItems(questionnaireResponse.item)}
                                        </div>
                                    </div>
                                ) : questionnaire ? (
                                    <div className="task-response__refero-container">
                                        <Provider store={store}>
                                            <div className="page_refero">
                                                <ReferoContainer
                                                    key={referoKey}
                                                    store={store}
                                                    questionnaire={questionnaire}
                                                    questionnaireResponse={questionnaireResponse}
                                                    onCancel={onClose}
                                                    onSave={(): void => {
                                                        // Disabled in read-only mode
                                                    }}
                                                    onPause={(): void => {
                                                        // Disabled in read-only mode
                                                    }}
                                                    onSubmit={(): void => {
                                                        // Disabled in read-only mode
                                                    }}
                                                    authorized={true}
                                                    resources={getResources(currentLanguage)}
                                                    sticky={false}
                                                    saveButtonDisabled={true}
                                                    readOnly={true}
                                                    syncQuestionnaireResponse={true}
                                                    validateScriptInjection
                                                />
                                            </div>
                                        </Provider>
                                    </div>
                                ) : (
                                    <div className="task-response__answers">
                                        {renderResponseItems(questionnaireResponse.item)}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="task-response__section">
                                <div className="task-response__empty">{t('No answers provided in this response.')}</div>
                            </div>
                        )}
                    </>
                )}

                {/* No Response State - Task not yet answered */}
                {!questionnaireResponse && !isLoading && !error && task.status === 'requested' && (
                    <div className="task-response__section">
                        <div className="task-response__empty task-response__empty--hint">
                            <p>
                                {isTherapyMaterial
                                    ? t('No response has been saved for this therapy material yet.')
                                    : isNachsorge
                                    ? t('No response has been saved for this aftercare task yet.')
                                    : t('This questionnaire has not been answered yet.')}
                            </p>
                            <p className="task-response__hint-text">
                                {isTherapyMaterial
                                    ? t('The therapy material remains available while its assignment is requested.')
                                    : isNachsorge
                                    ? t('The aftercare task remains available and can be answered again.')
                                    : t(
                                          'The task status is "requested" which means it is waiting for the patient to complete the questionnaire.',
                                      )}
                            </p>
                        </div>
                    </div>
                )}

                {/* No Response State - Other statuses */}
                {!questionnaireResponse && !isLoading && !error && task.status !== 'requested' && (
                    <div className="task-response__section">
                        <div className="task-response__empty">{t('No response available for this task.')}</div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TaskResponseModal;
